import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import VideoPlayer from "../../../components/video/VideoPlayer/VideoPlayer";
import {
  createMarketplaceProduct,
  getMarketplaceProduct,
  updateMarketplaceProduct,
} from "../../../features/marketplace/marketplaceProductApi";
import {
  CATEGORY_LABELS,
  LISTING_STATUSES,
  MODERATION_STATUSES,
  PRODUCT_CATEGORIES,
  PRODUCT_SUBTYPES,
  SUBTYPE_LABELS,
} from "../../../features/marketplace/marketplaceConstants";
import styles from "./MarketplaceProductWizard.module.scss";

const STEPS = ["Category & subtype", "Basics", "Media", "Type-specific fields"];
const IMAGE_LIMIT = 6;
const VIDEO_LIMIT = 2;
let mediaItemSeq = 0;

const DEFAULT_FORM = {
  category: PRODUCT_CATEGORIES.PHYSICAL,
  serviceType: "",
  digitalType: "",
  title: "",
  description: "",
  priceNoty: "",
  deliveryMethod: "",
  deliveryDescription: "",
  location: "",
  locationDescription: "",
  onlineMethod: "",
  methodDescription: "",
  digitalImagePackKeysRaw: "",
  digitalVideoId: "",
  digitalPdfKey: "",
};

const splitKeys = (value) =>
  String(value || "")
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const safeStr = (value) => (value == null ? "" : String(value));
const makeMediaItemId = (prefix) => `${prefix}-${Date.now()}-${mediaItemSeq++}`;

const extFromFile = (file) => {
  const name = safeStr(file?.name);
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : "";
};

async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        URL.revokeObjectURL(el.src || "");
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const mapProductToForm = (product) => ({
  ...DEFAULT_FORM,
  category: product?.category || PRODUCT_CATEGORIES.PHYSICAL,
  serviceType: product?.serviceType || "",
  digitalType: product?.digitalType || "",
  title: product?.title || "",
  description: product?.description || "",
  priceNoty: String(product?.priceNoty ?? ""),
  deliveryMethod: product?.deliveryMethod || "",
  deliveryDescription: product?.deliveryDescription || "",
  location: product?.location || "",
  locationDescription: product?.locationDescription || "",
  onlineMethod: product?.onlineMethod || "",
  methodDescription: product?.methodDescription || "",
  digitalImagePackKeysRaw: (product?.digitalImagePackKeys || []).join("\n"),
  digitalVideoId: product?.digitalVideoId || "",
  digitalPdfKey: product?.digitalPdfKey || "",
});

export default function MarketplaceProductWizard() {
  const { clubId, productId } = useParams();
  const navigate = useNavigate();
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("cm_at") || "";
  }, []);

  const isEdit = Boolean(productId);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [imageItems, setImageItems] = useState([]);
  const [videoItems, setVideoItems] = useState([]);
  const [uploadProgressPct, setUploadProgressPct] = useState(0);
  const [isUploadingForApproval, setIsUploadingForApproval] = useState(false);
  const [existingProductStatus, setExistingProductStatus] = useState({
    moderationStatus: "",
    status: "",
  });

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getMarketplaceProduct(clubId, productId);
        if (!mounted) return;
        const product = response?.product || {};
        setForm(mapProductToForm(product));
        setExistingProductStatus({
          moderationStatus: product?.moderationStatus || "",
          status: product?.status || "",
        });
        setImageItems(
          (product?.listingImageKeys || []).map((key, index) => ({
            id: makeMediaItemId("img-existing"),
            source: "existing",
            key,
            previewUrl: product?.listingImageUrls?.[index] || key,
          })),
        );
        setVideoItems(
          (product?.listingVideoIds || []).map((videoId) => ({
            id: makeMediaItemId("vid-existing"),
            source: "existing",
            videoId,
          })),
        );
      } catch (error) {
        if (!mounted) return;
        toast.error(
          error?.response?.data?.message || "Could not load product for edit.",
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [clubId, isEdit, productId]);

  useEffect(
    () => () => {
      imageItems.forEach((item) => {
        if (item?.source === "new" && item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      videoItems.forEach((item) => {
        if (item?.source === "new" && item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
    [imageItems, videoItems],
  );

  const subtypeOptions = useMemo(() => {
    if (form.category === PRODUCT_CATEGORIES.SERVICE) {
      return [PRODUCT_SUBTYPES.IN_PERSON, PRODUCT_SUBTYPES.ONLINE];
    }
    if (form.category === PRODUCT_CATEGORIES.DIGITAL) {
      return [
        PRODUCT_SUBTYPES.IMAGE_PACK,
        PRODUCT_SUBTYPES.VIDEO,
        PRODUCT_SUBTYPES.PDF,
      ];
    }
    return [];
  }, [form.category]);

  const selectedSubtype =
    form.category === PRODUCT_CATEGORIES.SERVICE
      ? form.serviceType
      : form.digitalType;

  const updateField = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

  const isApprovedAndActiveEdit =
    isEdit &&
    existingProductStatus.moderationStatus === MODERATION_STATUSES.APPROVED &&
    existingProductStatus.status === LISTING_STATUSES.ACTIVE;

  const isSubmitReady = useMemo(() => {
    const titleReady = form.title.trim().length > 0;
    const descriptionReady = form.description.trim().length > 0;
    const priceReady = Number(form.priceNoty) > 0;
    const hasImage = imageItems.length > 0;

    const subtypeReady =
      form.category === PRODUCT_CATEGORIES.PHYSICAL ||
      (form.category === PRODUCT_CATEGORIES.SERVICE &&
        Boolean(form.serviceType)) ||
      (form.category === PRODUCT_CATEGORIES.DIGITAL &&
        Boolean(form.digitalType));

    const typeFieldsReady =
      (form.category === PRODUCT_CATEGORIES.PHYSICAL &&
        form.deliveryMethod.trim().length > 0 &&
        form.deliveryDescription.trim().length > 0) ||
      (form.category === PRODUCT_CATEGORIES.SERVICE &&
        ((form.serviceType === PRODUCT_SUBTYPES.IN_PERSON &&
          form.location.trim().length > 0 &&
          form.locationDescription.trim().length > 0) ||
          (form.serviceType === PRODUCT_SUBTYPES.ONLINE &&
            form.onlineMethod.trim().length > 0 &&
            form.methodDescription.trim().length > 0))) ||
      (form.category === PRODUCT_CATEGORIES.DIGITAL &&
        ((form.digitalType === PRODUCT_SUBTYPES.IMAGE_PACK &&
          splitKeys(form.digitalImagePackKeysRaw).length > 0) ||
          (form.digitalType === PRODUCT_SUBTYPES.VIDEO &&
            form.digitalVideoId.trim().length > 0) ||
          (form.digitalType === PRODUCT_SUBTYPES.PDF &&
            form.digitalPdfKey.trim().length > 0)));

    return (
      currentStep === STEPS.length - 1 &&
      subtypeReady &&
      titleReady &&
      descriptionReady &&
      priceReady &&
      hasImage &&
      typeFieldsReady
    );
  }, [currentStep, form, imageItems.length]);

  const putToS3WithProgress = (uploadUrl, file, onPct, contentTypeOverride) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Type",
        contentTypeOverride || file?.type || "application/octet-stream",
      );

      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onPct?.(pct);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        reject(new Error(`Upload failed (${xhr.status})`));
      };

      xhr.onerror = () => reject(new Error("Upload failed (network error)"));
      xhr.send(file);
    });

  const presignImagePoster = async (file) => {
    const ext = extFromFile(file) || "jpg";
    const contentType = file?.type || "image/jpeg";
    const { data } = await api.post("/v1/posts/media/presign-image-poster", {
      ext,
      contentType,
    });
    return data;
  };

  const presignVideoViaVideos = async (file) => {
    const contentType = file?.type || "application/octet-stream";
    const filename =
      safeStr(file?.name) || `video.${extFromFile(file) || "mp4"}`;

    const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
      width: 0,
      height: 0,
      orientation: "landscape",
    };

    const { data } = await api.post("/v1/videos/presign", {
      contentType,
      filename,
      clientMeta,
    });

    return data;
  };

  const uploadPendingMedia = async ({ onProgress } = {}) => {
    const newImageItems = imageItems.filter((item) => item.source === "new");
    const newVideoItems = videoItems.filter((item) => item.source === "new");
    const totalUploads = newImageItems.length + newVideoItems.length;
    const progressByItem = {};
    const markProgress = (itemId, pct) => {
      progressByItem[itemId] = pct;
      if (!totalUploads) {
        onProgress?.(100);
        return;
      }
      const totalPct = Object.values(progressByItem).reduce(
        (sum, value) => sum + Number(value || 0),
        0,
      );
      onProgress?.(Math.round(totalPct / totalUploads));
    };

    const uploadedImageByItemId = {};
    const uploadedVideoByItemId = {};

    for (let i = 0; i < newImageItems.length; i += 1) {
      const item = newImageItems[i];
      const file = item.file;
      const presign = await presignImagePoster(file);
      await putToS3WithProgress(presign.uploadUrl, file, (pct) =>
        markProgress(item.id, pct),
      );
      if (presign?.processedKey) {
        uploadedImageByItemId[item.id] = presign.processedKey;
      }
      markProgress(item.id, 100);
    }

    for (let i = 0; i < newVideoItems.length; i += 1) {
      const item = newVideoItems[i];
      const file = item.file;
      const presign = await presignVideoViaVideos(file);
      const videoId = safeStr(presign?.id).trim();
      if (!videoId) throw new Error("Server did not return a video id.");

      await putToS3WithProgress(
        presign.url,
        file,
        (pct) => markProgress(item.id, pct),
        presign.contentType || file?.type,
      );
      await api.post(`/v1/videos/${videoId}/submit`);
      uploadedVideoByItemId[item.id] = videoId;
      markProgress(item.id, 100);
    }

    return { uploadedImageByItemId, uploadedVideoByItemId };
  };

  const buildPayload = (moderationStatus, media = {}) => {
    const listingImageKeys = imageItems
      .map((item) =>
        item.source === "existing"
          ? item.key
          : media.uploadedImageByItemId?.[item.id],
      )
      .filter(Boolean);
    const listingVideoIds = videoItems
      .map((item) =>
        item.source === "existing"
          ? item.videoId
          : media.uploadedVideoByItemId?.[item.id],
      )
      .filter(Boolean);

    const payload = {
      category: form.category,
      title: form.title.trim(),
      description: form.description.trim(),
      priceNoty: Number(form.priceNoty),
      listingImageKeys,
      listingVideoIds,
      status: "draft",
      moderationStatus,
    };

    if (form.category === PRODUCT_CATEGORIES.SERVICE) {
      payload.serviceType = form.serviceType || null;
      payload.location = form.location.trim() || null;
      payload.locationDescription = form.locationDescription.trim() || null;
      payload.onlineMethod = form.onlineMethod.trim() || null;
      payload.methodDescription = form.methodDescription.trim() || null;
    }

    if (form.category === PRODUCT_CATEGORIES.DIGITAL) {
      payload.digitalType = form.digitalType || null;
      payload.digitalImagePackKeys = splitKeys(form.digitalImagePackKeysRaw);
      payload.digitalVideoId = form.digitalVideoId.trim() || null;
      payload.digitalPdfKey = form.digitalPdfKey.trim() || null;
    }

    if (form.category === PRODUCT_CATEGORIES.PHYSICAL) {
      payload.deliveryMethod = form.deliveryMethod.trim() || null;
      payload.deliveryDescription = form.deliveryDescription.trim() || null;
    }

    return payload;
  };

  const save = async ({ moderationStatus, successMessage }) => {
    setSaving(true);
    try {
      const totalImages = imageItems.length;
      const totalVideos = videoItems.length;

      if (totalImages > IMAGE_LIMIT) {
        toast.error(`You can add up to ${IMAGE_LIMIT} listing images.`);
        return;
      }
      if (totalVideos > VIDEO_LIMIT) {
        toast.error(`You can add up to ${VIDEO_LIMIT} listing videos.`);
        return;
      }
      if (moderationStatus === MODERATION_STATUSES.PENDING && totalImages < 1) {
        toast.error("At least one photo is required before submitting.");
        return;
      }

      setUploadProgressPct(0);
      setIsUploadingForApproval(
        moderationStatus === MODERATION_STATUSES.PENDING,
      );
      const media = await uploadPendingMedia({
        onProgress:
          moderationStatus === MODERATION_STATUSES.PENDING
            ? setUploadProgressPct
            : null,
      });
      const payload = buildPayload(moderationStatus, media);
      let response;
      if (isEdit) {
        if (
          isApprovedAndActiveEdit &&
          moderationStatus === MODERATION_STATUSES.PENDING
        ) {
          await updateMarketplaceProduct(clubId, productId, {
            ...payload,
            moderationStatus: MODERATION_STATUSES.DRAFT,
            status: LISTING_STATUSES.DRAFT,
          });
        }
        response = await updateMarketplaceProduct(clubId, productId, payload);
      } else {
        response = await createMarketplaceProduct(clubId, payload);
      }

      toast.success(successMessage);
      setImageItems((prev) =>
        prev.map((item) =>
          item.source === "new"
            ? {
                ...item,
                source: "existing",
                key: media.uploadedImageByItemId?.[item.id],
              }
            : item,
        ),
      );
      setVideoItems((prev) =>
        prev.map((item) =>
          item.source === "new"
            ? {
                ...item,
                source: "existing",
                videoId: media.uploadedVideoByItemId?.[item.id],
              }
            : item,
        ),
      );
      if (moderationStatus === MODERATION_STATUSES.PENDING) {
        navigate("../..", { relative: "path" });
        return;
      }

      const savedId = response?.product?._id;
      if (!isEdit && savedId) {
        navigate(`../${savedId}/edit`, { replace: true });
        return;
      }
      navigate("..", { relative: "path" });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Could not save this product. Please check required fields.",
      );
    } finally {
      setIsUploadingForApproval(false);
      setSaving(false);
    }
  };

  const moveItem = (setter, index, direction) => {
    setter((prev) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  if (loading) {
    return (
      <section className={styles.page}>
        <div className={styles.bgGlowPink} />
        <div className={styles.bgGlowBlue} />
        <div className={styles.bgGrid} />

        <section className={styles.loadingCard}>
          <div className={styles.loadingSpinner} />
          <p>Loading product…</p>
        </section>
      </section>
    );
  }

  return (
    <section className={styles.page}>
      <div className={styles.bgGlowPink} />
      <div className={styles.bgGlowBlue} />
      <div className={styles.bgGrid} />

      <Link to="../.." relative="path" className={styles.back}>
        <span className={styles.backArrow}>←</span>
        <span>Back to seller dashboard</span>
      </Link>

      <header className={styles.hero}>
        <div className={styles.heroLeft}>
          <p className={styles.kicker}>Seller Product Studio</p>
          <h2>{isEdit ? "Edit Product" : "Create Product"}</h2>
          <p className={styles.heroText}>
            Complete all steps, then save as draft or submit for approval.
          </p>
        </div>

        <div className={styles.heroRight}>
          <div className={styles.heroStat}>
            <span className={styles.heroStatLabel}>Step</span>
            <strong className={styles.heroStatValue}>
              {currentStep + 1}/{STEPS.length}
            </strong>
          </div>
          {isUploadingForApproval ? (
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>Upload progress</span>
              <strong className={styles.heroStatValue}>
                {uploadProgressPct}%
              </strong>
            </div>
          ) : null}
        </div>
      </header>

      {isUploadingForApproval ? (
        <section className={styles.progressCard}>
          <div className={styles.progressHeader}>
            <strong>Submitting for approval…</strong>
            <span>{uploadProgressPct}%</span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${uploadProgressPct}%` }}
            />
          </div>
        </section>
      ) : null}

      <ol className={styles.steps}>
        {STEPS.map((label, index) => (
          <li
            key={label}
            className={`${styles.step} ${
              index === currentStep ? styles.active : ""
            } ${index < currentStep ? styles.complete : ""}`}
          >
            <span className={styles.stepIndex}>{index + 1}</span>
            <span className={styles.stepLabel}>{label}</span>
          </li>
        ))}
      </ol>

      {currentStep === 0 && (
        <section className={styles.card}>
          <div className={styles.cardGlowPink} />
          <div className={styles.cardGlowBlue} />

          <div className={styles.cardHeader}>
            <h3>Category setup</h3>
            <p>
              Choose the product type first so the form knows what fields to
              show.
            </p>
          </div>

          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Category</span>
              <select
                className={styles.input}
                value={form.category}
                onChange={(event) => {
                  const nextCategory = event.target.value;
                  setForm((prev) => ({
                    ...prev,
                    category: nextCategory,
                    serviceType: "",
                    digitalType: "",
                  }));
                }}
              >
                {Object.values(PRODUCT_CATEGORIES).map((category) => (
                  <option key={category} value={category}>
                    {CATEGORY_LABELS[category] || category}
                  </option>
                ))}
              </select>
            </label>

            {subtypeOptions.length > 0 && (
              <label className={styles.field}>
                <span className={styles.fieldLabel}>Subtype</span>
                <select
                  className={styles.input}
                  value={selectedSubtype}
                  onChange={(event) => {
                    if (form.category === PRODUCT_CATEGORIES.SERVICE) {
                      updateField("serviceType", event.target.value);
                    } else {
                      updateField("digitalType", event.target.value);
                    }
                  }}
                >
                  <option value="">Select subtype</option>
                  {subtypeOptions.map((subtype) => (
                    <option key={subtype} value={subtype}>
                      {SUBTYPE_LABELS[subtype] || subtype}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </section>
      )}

      {currentStep === 1 && (
        <section className={styles.card}>
          <div className={styles.cardGlowPink} />
          <div className={styles.cardGlowBlue} />

          <div className={styles.cardHeader}>
            <h3>Basic info</h3>
            <p>Set the main details that buyers will see first.</p>
          </div>

          <div className={styles.formGrid}>
            <label className={`${styles.field} ${styles.full}`}>
              <span className={styles.fieldLabel}>Title</span>
              <input
                className={styles.input}
                value={form.title}
                onChange={(event) => updateField("title", event.target.value)}
                placeholder="Product title"
              />
            </label>

            <label className={`${styles.field} ${styles.full}`}>
              <span className={styles.fieldLabel}>Description</span>
              <textarea
                className={`${styles.input} ${styles.textarea}`}
                value={form.description}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
                rows={5}
                placeholder="Describe your listing"
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>Price (Noty)</span>
              <input
                className={styles.input}
                type="number"
                min="1"
                value={form.priceNoty}
                onChange={(event) =>
                  updateField("priceNoty", event.target.value)
                }
                placeholder="100"
              />
            </label>
          </div>
        </section>
      )}

      {currentStep === 2 && (
        <section className={styles.card}>
          <div className={styles.cardGlowPink} />
          <div className={styles.cardGlowBlue} />

          <div className={styles.cardHeader}>
            <h3>Media</h3>
            <p>Add image keys and optional videos for a stronger listing.</p>
          </div>

          <div className={styles.formGrid}>
            <label className={`${styles.field} ${styles.full}`}>
              <span className={styles.fieldLabel}>
                Listing images (max {IMAGE_LIMIT})
              </span>
              <input
                className={styles.input}
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => {
                  const picked = Array.from(event.target.files || []);
                  if (!picked.length) return;
                  const remaining = Math.max(
                    0,
                    IMAGE_LIMIT - imageItems.length,
                  );
                  if (remaining <= 0) {
                    toast.error(`You can add up to ${IMAGE_LIMIT} images.`);
                    return;
                  }
                  const accepted = picked.slice(0, remaining);
                  setImageItems((prev) => [
                    ...prev,
                    ...accepted.map((file) => ({
                      id: makeMediaItemId("img-new"),
                      source: "new",
                      file,
                      previewUrl: URL.createObjectURL(file),
                    })),
                  ]);
                  if (picked.length > accepted.length) {
                    toast.info(
                      `Only ${remaining} more image(s) can be added for this listing.`,
                    );
                  }
                  event.target.value = "";
                }}
              />
              {!imageItems.length ? null : (
                <div className={styles.mediaPreviewGrid}>
                  {imageItems.map((item, index) => (
                    <article key={item.id} className={styles.mediaCard}>
                      <img
                        className={styles.previewImage}
                        src={item.previewUrl || item.key}
                        alt={`Listing image ${index + 1}`}
                      />
                      <p className={styles.mediaCaption}>
                        #{index + 1}{" "}
                        {item.source === "new"
                          ? item.file?.name || "New image"
                          : item.key}
                      </p>
                      <div className={styles.mediaActions}>
                        <button
                          type="button"
                          className={styles.pillRemove}
                          onClick={() => moveItem(setImageItems, index, -1)}
                          disabled={index === 0}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className={styles.pillRemove}
                          onClick={() => moveItem(setImageItems, index, 1)}
                          disabled={index >= imageItems.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className={styles.pillRemove}
                          onClick={() =>
                            setImageItems((prev) => {
                              const removed = prev[index];
                              if (
                                removed?.source === "new" &&
                                removed.previewUrl
                              ) {
                                URL.revokeObjectURL(removed.previewUrl);
                              }
                              return prev.filter((_, i) => i !== index);
                            })
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </label>

            <div className={`${styles.field} ${styles.full}`}>
              <span className={styles.fieldLabel}>
                Listing videos (optional, max {VIDEO_LIMIT})
              </span>

              <div className={styles.videoBlock}>
                <input
                  className={styles.input}
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={(event) => {
                    const picked = Array.from(event.target.files || []);
                    if (!picked.length) return;
                    const remaining = Math.max(
                      0,
                      VIDEO_LIMIT - videoItems.length,
                    );
                    if (remaining <= 0) {
                      toast.error(`You can add up to ${VIDEO_LIMIT} videos.`);
                      return;
                    }
                    const accepted = picked.slice(0, remaining);
                    setVideoItems((prev) => [
                      ...prev,
                      ...accepted.map((file) => ({
                        id: makeMediaItemId("vid-new"),
                        source: "new",
                        file,
                        previewUrl: URL.createObjectURL(file),
                      })),
                    ]);
                    if (picked.length > accepted.length) {
                      toast.info(
                        `Only ${remaining} more video(s) can be added for this listing.`,
                      );
                    }
                    event.target.value = "";
                  }}
                />
              </div>

              {!videoItems.length ? null : (
                <div className={styles.mediaPreviewGrid}>
                  {videoItems.map((item, index) => (
                    <article key={item.id} className={styles.mediaCard}>
                      {item.source === "new" ? (
                        <video
                          className={styles.previewVideo}
                          src={item.previewUrl}
                          controls
                          muted
                        />
                      ) : (
                        <VideoPlayer
                          token={token}
                          videoId={item.videoId}
                          mode="proxy"
                          className={styles.previewVideo}
                          controls
                          muted
                        />
                      )}
                      <p className={styles.mediaCaption}>
                        #{index + 1}{" "}
                        {item.source === "new"
                          ? item.file?.name || "New video"
                          : item.videoId}
                      </p>
                      <div className={styles.mediaActions}>
                        <button
                          type="button"
                          className={styles.pillRemove}
                          onClick={() => moveItem(setVideoItems, index, -1)}
                          disabled={index === 0}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className={styles.pillRemove}
                          onClick={() => moveItem(setVideoItems, index, 1)}
                          disabled={index >= videoItems.length - 1}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className={styles.pillRemove}
                          onClick={() =>
                            setVideoItems((prev) => {
                              const removed = prev[index];
                              if (
                                removed?.source === "new" &&
                                removed.previewUrl
                              ) {
                                URL.revokeObjectURL(removed.previewUrl);
                              }
                              return prev.filter((_, i) => i !== index);
                            })
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {currentStep === 3 && (
        <section className={styles.card}>
          <div className={styles.cardGlowPink} />
          <div className={styles.cardGlowBlue} />

          <div className={styles.cardHeader}>
            <h3>Type-specific fields</h3>
            <p>
              Finish the details based on the category and subtype you selected.
            </p>
          </div>

          <div className={styles.formGrid}>
            {form.category === PRODUCT_CATEGORIES.PHYSICAL && (
              <>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Delivery method</span>
                  <input
                    className={styles.input}
                    value={form.deliveryMethod}
                    onChange={(event) =>
                      updateField("deliveryMethod", event.target.value)
                    }
                    placeholder="Pickup, shipping, local dropoff..."
                  />
                </label>

                <label className={`${styles.field} ${styles.full}`}>
                  <span className={styles.fieldLabel}>Delivery details</span>
                  <textarea
                    className={`${styles.input} ${styles.textarea}`}
                    value={form.deliveryDescription}
                    onChange={(event) =>
                      updateField("deliveryDescription", event.target.value)
                    }
                    rows={4}
                  />
                </label>
              </>
            )}

            {form.category === PRODUCT_CATEGORIES.SERVICE && (
              <>
                {form.serviceType === PRODUCT_SUBTYPES.IN_PERSON ? (
                  <>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>Location</span>
                      <input
                        className={styles.input}
                        value={form.location}
                        onChange={(event) =>
                          updateField("location", event.target.value)
                        }
                      />
                    </label>

                    <label className={`${styles.field} ${styles.full}`}>
                      <span className={styles.fieldLabel}>
                        Location details
                      </span>
                      <textarea
                        className={`${styles.input} ${styles.textarea}`}
                        value={form.locationDescription}
                        onChange={(event) =>
                          updateField("locationDescription", event.target.value)
                        }
                        rows={4}
                      />
                    </label>
                  </>
                ) : (
                  <>
                    <label className={styles.field}>
                      <span className={styles.fieldLabel}>Online method</span>
                      <input
                        className={styles.input}
                        value={form.onlineMethod}
                        onChange={(event) =>
                          updateField("onlineMethod", event.target.value)
                        }
                        placeholder="Zoom, Telegram, etc"
                      />
                    </label>

                    <label className={`${styles.field} ${styles.full}`}>
                      <span className={styles.fieldLabel}>Method details</span>
                      <textarea
                        className={`${styles.input} ${styles.textarea}`}
                        value={form.methodDescription}
                        onChange={(event) =>
                          updateField("methodDescription", event.target.value)
                        }
                        rows={4}
                      />
                    </label>
                  </>
                )}
              </>
            )}

            {form.category === PRODUCT_CATEGORIES.DIGITAL && (
              <>
                {form.digitalType === PRODUCT_SUBTYPES.IMAGE_PACK && (
                  <label className={`${styles.field} ${styles.full}`}>
                    <span className={styles.fieldLabel}>Image pack keys</span>
                    <textarea
                      className={`${styles.input} ${styles.textarea}`}
                      value={form.digitalImagePackKeysRaw}
                      onChange={(event) =>
                        updateField(
                          "digitalImagePackKeysRaw",
                          event.target.value,
                        )
                      }
                      rows={4}
                    />
                  </label>
                )}

                {form.digitalType === PRODUCT_SUBTYPES.VIDEO && (
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Digital video ID</span>
                    <input
                      className={styles.input}
                      value={form.digitalVideoId}
                      onChange={(event) =>
                        updateField("digitalVideoId", event.target.value)
                      }
                      placeholder="Paste uploaded video ID"
                    />
                  </label>
                )}

                {form.digitalType === PRODUCT_SUBTYPES.PDF && (
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>PDF key</span>
                    <input
                      className={styles.input}
                      value={form.digitalPdfKey}
                      onChange={(event) =>
                        updateField("digitalPdfKey", event.target.value)
                      }
                      placeholder="processed/private/.../file.pdf"
                    />
                  </label>
                )}
              </>
            )}
          </div>
        </section>
      )}

      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
          disabled={currentStep === 0}
        >
          Back
        </button>

        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() =>
            setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1))
          }
          disabled={currentStep >= STEPS.length - 1}
        >
          Next
        </button>

        {!isApprovedAndActiveEdit && (
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() =>
              save({
                moderationStatus: MODERATION_STATUSES.DRAFT,
                successMessage: "Draft saved.",
              })
            }
            disabled={saving}
          >
            Save Draft
          </button>
        )}

        <button
          type="button"
          className={styles.submitBtn}
          onClick={() =>
            save({
              moderationStatus: MODERATION_STATUSES.PENDING,
              successMessage: "Submitted for approval.",
            })
          }
          disabled={saving || !isSubmitReady}
        >
          {saving ? "Saving..." : "Submit for Approval"}
        </button>
      </footer>
    </section>
  );
}
